import { useCallback, useEffect, useState } from "react";
import { roomApi, toSubRoom } from "./roomApi";

// A backstop under the stream, which announces every change as it happens.
const POLL_MS = 10000;

let localSeq = 0;

/**
 * A session's sub-rooms — breakout rooms — and which one I am in.
 *
 * The teacher arranges them (`open` with a whole plan, `close` to bring
 * everyone back); everyone else only follows. The class stream announces
 * each change (`applyAnnounced`), so a student moves the moment the
 * teacher presses the button, with a slow poll underneath in case the
 * stream is down.
 *
 * Without a session it keeps the rooms in memory, which is what a preview
 * of the room wants.
 */
export function useSubRooms({ sessionGuid, currentUserGuid }) {
  const [rooms, setRooms] = useState([]);
  const [myRoomId, setMyRoomId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const live = Boolean(sessionGuid);

  const accept = useCallback(
    (rows, mine) => {
      const next = (rows || []).map(toSubRoom);
      setRooms(next);
      // Worked out from the members when the server has not said, so an
      // announcement reaches everyone without each asking again.
      setMyRoomId(
        mine !== undefined
          ? mine
          : next.find((r) => r.members.some((m) => m.guid === currentUserGuid))?.id || null
      );
    },
    [currentUserGuid]
  );

  const refresh = useCallback(async () => {
    if (!live) return;
    try {
      const data = await roomApi.getSubRooms(sessionGuid);
      accept(data.rooms, data.my_room_guid || null);
    } catch {
      /* keep what we have */
    }
  }, [live, sessionGuid, accept]);

  useEffect(() => {
    if (!live) return undefined;
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [live, refresh]);

  /** What the class stream said. */
  const applyAnnounced = useCallback((rows) => accept(rows), [accept]);

  /**
   * Open or rearrange the rooms. `plan` is [{ id?, name, memberGuids }]:
   * a room with an id is one already open, and keeps its board.
   */
  const open = useCallback(
    async (plan) => {
      setError(null);
      if (!live) {
        const next = plan.map((room) => ({
          id: room.id || `local-room-${(localSeq += 1)}`,
          name: room.name,
          members: room.members || room.memberGuids.map((guid) => ({ guid, name: guid })),
        }));
        setRooms(next);
        setMyRoomId(next.find((r) => r.members.some((m) => m.guid === currentUserGuid))?.id || null);
        return true;
      }
      setBusy(true);
      try {
        const data = await roomApi.planSubRooms(
          sessionGuid,
          plan.map((room) => ({ guid: room.id || null, name: room.name, user_guids: room.memberGuids }))
        );
        accept(data.rooms, null);
        return true;
      } catch (e) {
        setError(e.body?.detail || "The rooms could not be opened. Try again.");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [live, sessionGuid, accept, currentUserGuid]
  );

  /** Everyone back to the class. */
  const close = useCallback(async () => {
    setError(null);
    if (!live) {
      setRooms([]);
      setMyRoomId(null);
      return true;
    }
    setBusy(true);
    try {
      const data = await roomApi.closeSubRooms(sessionGuid);
      accept(data.rooms, null);
      return true;
    } catch (e) {
      setError(e.body?.detail || "The rooms could not be closed. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [live, sessionGuid, accept]);

  return { rooms, myRoomId, isOpen: rooms.length > 0, busy, error, open, close, refresh, applyAnnounced };
}
